import * as Sentry from "@sentry/react";

/**
 * Configuration Sentry pour le monitoring d'erreurs uniquement
 * Suivant les bonnes pratiques de l'architecture existante
 */
export const initSentry = () => {
  // Ne pas initialiser Sentry en développement local (optionnel)
  const isDevelopment = import.meta.env.VITE_ENVIRONMENT === "development";
  const environment = import.meta.env.VITE_ENVIRONMENT;
  const sentryDsn = import.meta.env.VITE_SENTRY_DSN;

  // Si pas de DSN configuré, on ne fait rien
  if (!sentryDsn) {
    console.warn(
      "⚠️  Sentry DSN non configuré. Le monitoring d'erreurs est désactivé.",
    );
    return;
  }

  Sentry.init({
    dsn: sentryDsn,

    // Environnement (dev, staging, production)
    environment: environment || "development",

    // Informations sur l'application
    release: `portal@${import.meta.env.VITE_APP_NAME || "PayForm"}`,

    // ❌ Performance monitoring / Tracing DÉSACTIVÉ
    // tracesSampleRate: 0 désactive complètement le tracing
    tracesSampleRate: 0,

    // ❌ Session Replay DÉSACTIVÉ
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,

    // ❌ Logs monitoring DÉSACTIVÉ
    enableLogs: false,

    // ✅ Intégrations minimales pour monitoring d'erreurs UNIQUEMENT
    integrations: [
      // Capture des erreurs non gérées (error monitoring uniquement)
      Sentry.globalHandlersIntegration({
        onerror: true,
        onunhandledrejection: true,
      }),

      // Instrumentation HTTP pour les breadcrumbs SEULEMENT (pas de tracing)
      // Cette intégration ne fait que capturer les breadcrumbs, pas le tracing
      Sentry.httpClientIntegration({
        failedRequestStatusCodes: [[500, 599]], // Breadcrumbs pour erreurs HTTP uniquement
      }),

      // ❌ NE PAS inclure browserTracingIntegration (activerait le tracing)
      // ❌ NE PAS inclure replayIntegration (activerait le replay)
      // ❌ NE PAS inclure consoleLoggingIntegration (activerait les logs)
    ],

    // Options de confidentialité
    sendDefaultPii: false, // Ne pas envoyer d'infos personnelles par défaut

    // Ignorer certaines erreurs courantes/non critiques
    ignoreErrors: [
      // Erreurs de navigateur courantes
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications",
      // Erreurs d'extension de navigateur
      "Can't find variable: __firefox__",
      "Can't find variable: __chrome__",
      // Erreurs réseau courantes (à décommenter si trop de bruit)
      // "Network request failed",
      // "NetworkError",
      // "Failed to fetch",
      // Erreurs de chargement de scripts (ad-blockers)
      "ChunkLoadError",
      "Loading chunk",
      // Erreurs courantes React
      "cancelled",
    ],

    // Filtrer les URLs à ne pas tracer
    denyUrls: [
      // Extensions de navigateur
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
      // Scripts tiers
      /googletagmanager\.com/i,
      /google-analytics\.com/i,
    ],

    // Options de debug (uniquement en dev)
    debug: isDevelopment,

    // Hook avant envoi (pour filtrer/modifier les événements)
    beforeSend(event, hint) {
      //   En développement, logger dans la console au lieu d'envoyer
      if (isDevelopment) {
        console.group("🔍 Sentry Event (Dev Mode - Not Sent)");
        console.log("Event:", event);
        console.log("Hint:", hint);
        console.groupEnd();
        return null; // Ne pas envoyer en dev
      }

      return event;
    },
  });
};

/**
 * Définir le contexte utilisateur pour tracer les erreurs
 */
export const setSentryUser = (user: {
  id: string;
  email: string;
  subscriptionPlan?: string | null;
  subscriptionStatus?: string | null;
}) => {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    subscription_plan: user.subscriptionPlan || "none",
    subscription_status: user.subscriptionStatus || "inactive",
  });
};

/**
 * Effacer le contexte utilisateur (lors de la déconnexion)
 */
export const clearSentryUser = () => {
  Sentry.setUser(null);
};

/**
 * Capturer manuellement une exception
 */
export const captureException = (
  error: Error,
  context?: Record<string, unknown>,
) => {
  if (context) {
    Sentry.setContext("additional_context", context);
  }
  Sentry.captureException(error);
  console.error("🚨 Exception capturée par Sentry:", error);
};

/**
 * Ajouter un breadcrumb (trace d'événements)
 */
export const addBreadcrumb = (
  message: string,
  data?: Record<string, unknown>,
) => {
  Sentry.addBreadcrumb({
    message,
    data,
    level: "info",
    timestamp: Date.now() / 1000,
  });
};

/**
 * Capturer un message (pour des logs non-erreur)
 */
export const captureMessage = (
  message: string,
  level: "info" | "warning" | "error" = "info",
) => {
  Sentry.captureMessage(message, level);
};

/**
 * Capturer une erreur fatale (niveau critique)
 */
export const captureFatalException = (
  error: Error,
  context?: Record<string, unknown>,
) => {
  Sentry.withScope((scope) => {
    scope.setLevel("fatal");
    if (context) {
      scope.setContext("additional_context", context);
    }
    scope.setTag("critical", "true");
    Sentry.captureException(error);
  });
  console.error("🚨 Erreur FATALE capturée par Sentry:", error);
};
